import { ActionIcon, Box, Group, Paper, Text } from '@mantine/core';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import RichAudioPlayer from '../components/rich_audio_player';

interface GlobalAudioPlayerValue {
  openAudio: (url: string, title?: string) => void;
  closeAudio: () => void;
  currentUrl: string | null;
  isPlaying: boolean;
}

interface GlobalAudioState {
  url: string | null;
  title: string;
  isPlaying: boolean;
  volume: number;
  speed: number;
}

const GlobalAudioPlayerContext = createContext<GlobalAudioPlayerValue | null>(null);

export function GlobalAudioPlayerProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<GlobalAudioState>({
    url: null,
    title: 'Audio playback',
    isPlaying: false,
    volume: 100,
    speed: 1,
  });

  const openAudio = useCallback((url: string, title = 'Audio playback') => {
    setState((prev) => ({
      ...prev,
      url,
      title,
      isPlaying: true,
    }));
  }, []);

  const closeAudio = useCallback(() => {
    setState((prev) => ({ ...prev, url: null, isPlaying: false }));
  }, []);

  const value = useMemo(
    () => ({
      openAudio,
      closeAudio,
      currentUrl: state.url,
      isPlaying: state.isPlaying,
    }),
    [openAudio, closeAudio, state.url, state.isPlaying],
  );

  return (
    <GlobalAudioPlayerContext.Provider value={value}>
      {children}

      {state.url && (
        <Box
          style={{
            position: 'fixed',
            top: 16,
            right: 16,
            width: 380,
            maxWidth: 'calc(100vw - 24px)',
            zIndex: 999,
          }}
        >
          <Paper
            radius="md"
            p="sm"
            shadow="lg"
            style={{
              border: '1px solid rgba(120, 133, 163, 0.28)',
              background: 'rgba(13, 23, 45, 0.88)',
              backdropFilter: 'blur(6px)',
            }}
          >
            <Group justify="space-between" mb="xs">
              <Text size="sm" fw={600} c="white">Now playing</Text>
              <ActionIcon variant="subtle" color="gray" onClick={closeAudio} aria-label="Close player">
                <FaTimes />
              </ActionIcon>
            </Group>

            <RichAudioPlayer
              src={state.url}
              title={state.title}
              autoPlay
              persistedVolume={state.volume}
              persistedSpeed={state.speed}
              onVolumeChange={(volume) => setState((prev) => ({ ...prev, volume }))}
              onSpeedChange={(speed) => setState((prev) => ({ ...prev, speed }))}
              onPlayStateChange={(isPlaying) => setState((prev) => ({ ...prev, isPlaying }))}
            />
          </Paper>
        </Box>
      )}
    </GlobalAudioPlayerContext.Provider>
  );
}

export function useGlobalAudioPlayer() {
  const context = useContext(GlobalAudioPlayerContext);
  if (!context) {
    throw new Error('useGlobalAudioPlayer must be used inside GlobalAudioPlayerProvider');
  }

  return context;
}
